"use client";

import {NeoButton, NeoTextBox} from "@/components/forms";
import {NeoError, NeoSubHeader} from "@/components/shared";
import {authService} from "@/service/auth.service";
import {profileService} from "@/service/profile.service";
import {yupResolver} from "@hookform/resolvers/yup";
import router from "next/router";
import {useState} from "react";
import {useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {AiOutlineLoading} from "react-icons/ai";
import * as yup from "yup";

interface ChangePasswordProps {
  mobileNumber: number;
  onClose: Function;
}

interface IChangePassword {
  oldpassword: string;
  newpassword: string;
  confirmPassword: string;
  otp: string;
}

const defaultValues = {
  oldpassword: "",
  newpassword: "",
  confirmPassword: "",
  otp: ""
};

export const ChangePasswordForm: React.FC<ChangePasswordProps> = ({
  mobileNumber,
  onClose
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showOTP, setShowOTP] = useState(false);
  const [loading, setLoading] = useState(false);

  const validationSchema = yup.object().shape({
    oldpassword: yup
      .string()
      .required("Old password is required")
      .min(8, "Password must be at least 8 characters"),
    newpassword: yup
      .string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters"),
    confirmpassword: yup
      .string()
      .required("Confirm Password is required")
      .min(8, "Password must be at least 8 characters")
      .oneOf([yup.ref("newpassword"), ""], "Passwords must match"),
    otp: yup
      .string()
      .required("OTP is required")
      .min(0, "OTP must be at least 4 numbers")
      .max(9999)
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
  });

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: {isSubmitting, errors},
    reset: resetForm
  } = useForm<any>({
    defaultValues,
    mode: "onTouched",
    resolver: yupResolver(validationSchema)
  });

  const onSubmit = async (data: IChangePassword) => {
    setLoading(true);
    await profileService
      .changePassword({
        otp: data.otp,
        password: data.confirmPassword
      })
      .then((response: any) => {
        if (response?.data?.status === "success") {
          onClose();
          router.push("/auth/login");
        } else {
          setErrorMessage(
            "Sorry!, We have technical issue please try again later"
          );
        }
      })
      .catch(err => {
        if (err?.errors.length > 0) {
          err?.errors.forEach(
            ({field, message}: {field: any; message: string}) => {
              setError(field, {type: "server", message: message});
            }
          );
        }
      })
      .finally(() => {
        setLoading(false);
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  const handleOTP = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setLoading(true);
    await authService
      .fetchOTP({mobileNumber: mobileNumber})
      .then((response: any) => {
        if (response?.data?.status === "success") {
          toast.success("Please check your phone, your OTP is sent");
          setShowOTP(true);
        } else {
          setErrorMessage(
            "Error while sending OTP, Please check your mobile number and try again"
          );
        }
      })
      .catch(err => {
        if (err?.errors.length > 0) {
          err?.errors.forEach(
            ({field, message}: {field: any; message: string}) => {
              setError(field, {type: "server", message: message});
            }
          );
        }
      })
      .finally(() => {
        setLoading(false);
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  return (
    <>
      <div className="mx-2 my-4">
        <NeoSubHeader message="Change Password"></NeoSubHeader>
      </div>
      <form
        onSubmit={handleSubmit(showOTP ? onSubmit : handleOTP)}
        className="mx-6"
      >
        {/* Old Password Field*/}
        <div className="py-2">
          <NeoTextBox
            label="Old Password"
            id="oldpassword"
            type="password"
            placeholder="Please enter old password"
            color={errors.oldpassword ? "danger" : "default"}
            isInvalid={errors.oldpassword ? true : false}
            errorMessage={errors.oldpassword && `${errors.oldpassword.message}`}
            {...register("oldpassword")}
          />
        </div>
        {/* New Password Field*/}
        <div className="py-2">
          <NeoTextBox
            label="New Password"
            id="newpassword"
            type="password"
            placeholder="Please enter new password"
            color={errors.newpassword ? "danger" : "default"}
            isInvalid={errors.newpassword ? true : false}
            errorMessage={errors.newpassword && `${errors.newpassword.message}`}
            {...register("newpassword")}
          />
        </div>
        {/* Confirm Password Field*/}
        <div className="py-2">
          <NeoTextBox
            label="Confirm Password"
            id="confirmpassword"
            type="password"
            placeholder="Please enter confirm password"
            color={errors.confirmpassword ? "danger" : "default"}
            isInvalid={errors.confirmpassword ? true : false}
            errorMessage={
              errors.confirmpassword && `${errors.confirmpassword.message}`
            }
            {...register("confirmpassword")}
          />
        </div>
        {showOTP && (
          <div className="py-2">
            <NeoTextBox
              label="OTP"
              id="otp"
              size="md"
              radius="md"
              placeholder="Please enter OTP"
              color={errors.otp ? "danger" : "default"}
              isInvalid={errors.otp ? true : false}
              errorMessage={errors.otp && `${errors.otp.message}`}
              {...register("otp")}
            />
          </div>
        )}
        {errorMessage ? <NeoError message={errorMessage}></NeoError> : null}
        <div className="flex items-center justify-end p-4">
          {!showOTP ? (
            <NeoButton
              className="mx-5 mt-2"
              type="submit"
              disabled={isSubmitting}
              isLoading={loading}
              spinner={
                <AiOutlineLoading size="xs" className="animate-spin h-6 w-6" />
              }
              color="black"
            >
              Next
            </NeoButton>
          ) : (
            <>
              <NeoButton
                className="mx-5 mt-2"
                type="submit"
                disabled={isSubmitting}
                isLoading={loading}
                spinner={
                  <AiOutlineLoading
                    size="xs"
                    className="animate-spin h-6 w-6"
                  />
                }
                color="black"
              >
                Submit
              </NeoButton>
              <NeoButton
                className="mt-2"
                color="white"
                type="button"
                onClick={() => setShowOTP(false)}
              >
                Cancel
              </NeoButton>
            </>
          )}
        </div>
      </form>
    </>
  );
};
